import {
  buildDataTableCols,
  createCheckboxPreset,
  type DataTableCol
} from '@/components/custom/erp/data-table-v2/column-builder';
import { buildInputTableCols, type InputTableCol } from '@/components/custom/erp/input-table-v2';
import { ConversionQuantityFieldV2, DvtFieldV2 } from '@/components/custom/erp/dvt-field-v2';
import { formatDate, formatMoney, formatRoundedNumber } from '@/lib/formatUtils';
import type { DataTableColumn } from '@/components/custom/erp/data-table-v2';
import { Checkbox } from '@/components/ui/checkbox';

type HoaDonMuaHangRow = MuaHangRecord;

const STATUS_OPTIONS = [
  { value: '0', label: 'Lập chứng từ' },
  { value: '3', label: 'Chờ duyệt' },
  { value: '5', label: 'Xuất hóa đơn' }
];

export const DATA_TABLE_BASE_COLS: DataTableCol<HoaDonMuaHangRow>[] = [
  {
    field: 'hdmh_yn',
    headerName: 'Hoá đơn',
    width: 90,
    preset: createCheckboxPreset({
      getCheckedValue: row => Boolean(row?.hdmh_yn),
      renderCheckbox: checked => <Checkbox checked={checked} />
    })
  },
  {
    field: 'pn_yn',
    headerName: 'Nhập kho',
    width: 90,
    preset: createCheckboxPreset({
      getCheckedValue: row => Boolean(row?.pn_yn),
      renderCheckbox: checked => <Checkbox checked={checked} />
    })
  },
  {
    field: 'xt_yn',
    headerName: 'Xuất thẳng',
    width: 90,
    preset: createCheckboxPreset({
      getCheckedValue: row => Boolean(row?.xt_yn),
      renderCheckbox: checked => <Checkbox checked={checked} />
    })
  },
  {
    field: 'status',
    headerName: 'Trạng thái',
    width: 130,
    valueGetter: row => row?.status,
    valueFormatter: value => STATUS_OPTIONS.find(option => option.value === String(value ?? ''))?.label ?? '',
    filter: { type: 'multiple', options: STATUS_OPTIONS }
  },
  { field: 'so_ct', headerName: 'Số c/từ', width: 140, valueGetter: row => row?.so_ct },
  {
    field: 'ngay_ct',
    headerName: 'Ngày c/từ',
    width: 100,
    valueGetter: row => row?.ngay_ct,
    valueFormatter: value => formatDate(value as MuaHangRecord),
    filter: { type: 'date' }
  },
  {
    field: 'so_ct0',
    headerName: 'Số hóa đơn',
    width: 120,
    valueGetter: row => row?.so_ct0
  },
  {
    field: 'ma_kh',
    headerName: 'Mã khách hàng',
    width: 120,
    valueGetter: row => row?.ma_kh_data?.customer_code || row?.ma_kh || ''
  },
  {
    field: 'ten_kh',
    headerName: 'Tên khách hàng',
    width: 200,
    valueGetter: row => row?.ma_kh_data?.customer_name || row?.ten_kh || ''
  },
  {
    field: 'dien_giai',
    headerName: 'Diễn giải',
    width: 200,
    valueGetter: row => row?.dien_giai
  },
  {
    field: 'tk',
    headerName: 'Tk có',
    width: 100,
    headerAlign: 'center',
    align: 'center',
    valueGetter: row => row?.tk_data?.code || row?.tk || ''
  },
  {
    field: 't_tt_nt',
    headerName: 'Tổng thanh toán',
    width: 150,
    columnType: 'number',
    align: 'right',
    valueGetter: row => row?.t_tt_nt,
    valueFormatter: value => formatRoundedNumber(Number(value ?? 0)),
    filter: { type: 'text' }
  },
  {
    field: 'ma_nt',
    headerName: 'Ngoại tệ',
    width: 120,
    align: 'center',
    valueGetter: row => row?.ma_nt_data?.ma_nt || row?.ma_nt || ''
  }
];

export const DATA_TABLE_COLUMNS = (
  handleViewClick: (row?: HoaDonMuaHangRow) => void,
  totalAmount?: number | null,
  isLoading?: boolean
): DataTableColumn[] => {
  const total = (
    <div className='flex items-center gap-2'>
      <span className='text-lg font-bold'>&#931;</span>
      <span className='font-semibold text-main'>
        {isLoading ? 'Đang tính...' : typeof totalAmount === 'number' ? formatRoundedNumber(totalAmount) : '—'}
      </span>
    </div>
  );

  return buildDataTableCols(
    DATA_TABLE_BASE_COLS.map(col => {
      if (col.field === 'so_ct') {
        return {
          ...col,
          renderCell: params => (
            <button
              onClick={event => {
                event.stopPropagation();
                handleViewClick(params.row);
              }}
              className='hover:text-blue-500 hover:underline'
            >
              {params.row?.so_ct}
            </button>
          )
        };
      }

      if (col.field === 't_tt_nt') {
        return { ...col, filter: { type: 'text' as const, after: total } };
      }

      return col;
    })
  );
};

export const INPUT_TABLE_BASE_COLS: InputTableCol<HoaDonMuaHangRow>[] = [
  {
    field: 'ma_vt',
    headerName: 'Mã sản phẩm',
    width: 120,
    renderCell: params => params.row.ma_vt_data?.ma_vt || params.row.ma_vt
  },
  {
    field: 'ten_vt',
    headerName: 'Tên sản phẩm',
    width: 200,
    renderCell: params => params.row.ma_vt_data?.ten_vt
  },
  {
    field: 'dvt',
    headerName: 'Đvt',
    width: 100,
    renderCell: params => <DvtFieldV2 row={params.row} />
  },
  {
    field: 'ma_kho',
    headerName: 'Mã kho',
    width: 120,
    headerAlign: 'center',
    align: 'center',
    renderCell: params => params.row.ma_kho_data?.ma_kho
  },
  {
    field: 'so_luong',
    headerName: 'Số lượng',
    width: 130,
    renderCell: params => <ConversionQuantityFieldV2 row={params.row} />
  },
  {
    field: 'gia_nt0',
    headerName: 'Giá VND',
    width: 150,
    headerAlign: 'center',
    align: 'right',
    renderCell: params => formatMoney(params.row.gia_nt0 || 0)
  },
  {
    field: 'tien_nt0',
    headerName: 'Tiền hàng VND',
    width: 150,
    headerAlign: 'center',
    align: 'right',
    renderCell: params => formatMoney(params.row.tien_nt0 || 0)
  },
  {
    field: 'cp_nt',
    headerName: 'Chi phí VND',
    width: 150,
    headerAlign: 'center',
    align: 'right',
    renderCell: params => formatMoney(params.row.cp_nt || 0)
  },
  {
    field: 'gia_nt',
    headerName: 'Giá tồn VND',
    width: 150,
    headerAlign: 'center',
    align: 'right',
    renderCell: params => formatMoney(params.row.gia_nt || 0)
  },
  {
    field: 'ma_thue',
    headerName: 'Thuế suất',
    width: 150,
    renderCell: params => params.row.ma_thue_data?.ten_thue
  },
  {
    field: 'thue_nt',
    headerName: 'Thuế GTGT VND',
    width: 150,
    renderCell: params => params.row.thue_nt?.toLocaleString('vi-VN') || '0'
  },
  {
    field: 'tk_cpxt',
    headerName: 'Tk cp xuất thẳng',
    width: 150,
    renderCell: params => params.row.tk_cpxt_data?.code
  },
  {
    field: 'px_dd',
    headerName: 'Đích danh',
    width: 120,
    columnType: 'boolean',
    align: 'center',
    renderCell: params => <Checkbox checked={params.row.px_dd} />
  },
  {
    field: 'tk_vt',
    headerName: 'Tk nợ',
    width: 150,
    headerAlign: 'center',
    align: 'center',
    renderCell: params => params.row.tk_vt_data?.code
  },
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 120,
    headerAlign: 'center',
    align: 'center',
    renderCell: params => params.row.ma_bp_data?.ma_bp
  },
  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 120,
    headerAlign: 'center',
    align: 'center',
    renderCell: params => params.row.ma_vv_data?.ma_vv
  },
  {
    field: 'ma_hd',
    headerName: 'Hợp đồng',
    width: 120,
    headerAlign: 'center',
    align: 'center',
    renderCell: params => params.row.ma_hd_data?.ma_hd
  },
  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 120,
    headerAlign: 'center',
    align: 'center',
    renderCell: params => params.row.ma_dtt_data?.ma_dtt
  },
  {
    field: 'ma_ku',
    headerName: 'Khế ước',
    width: 120,
    headerAlign: 'center',
    align: 'center',
    renderCell: params => params.row.ma_ku_data?.ma_ku
  },
  {
    field: 'ma_phi',
    headerName: 'Phí',
    width: 120,
    headerAlign: 'center',
    align: 'center',
    renderCell: params => params.row.ma_phi_data?.ma_phi
  },
  {
    field: 'ma_cp0',
    headerName: 'C/p không h/lệ',
    width: 120,
    headerAlign: 'center',
    align: 'center',
    renderCell: params => params.row.ma_cp0_data?.ma_cp
  },
  {
    field: 'id_dh',
    headerName: 'Số đơn hàng',
    width: 120,
    renderCell: params => params.row.id_dh?.so_ct
  },
  {
    field: 'line_dh',
    headerName: 'Dòng',
    width: 120,
    renderCell: params => params.row.line_dh?.line
  },
  {
    field: 'so_ct_hd4',
    headerName: 'Số hóa đơn',
    width: 120,
    renderCell: params => params.row.so_ct_hd4
  },
  {
    field: 'line_dh4',
    headerName: 'Dòng',
    width: 120,
    renderCell: params => params.row.line_dh4
  }
];

export const INPUT_TABLE_COLUMNS = buildInputTableCols(INPUT_TABLE_BASE_COLS);

export const historyColumns: DataTableColumn[] = [
  { field: 'username', headerName: 'Tài khoản', width: 100 },
  { field: 'nickname', headerName: 'Tên tài khoản', width: 200 },
  { field: 'action', headerName: 'Hành động', width: 100 },
  { field: 'datetime0', headerName: 'Thời gian', width: 150 }
];
